import {
  AbsurdIpfError,
  IpfError,
} from '@ipfabric/frontend-shared-assets/utils/IpfError';
import {
  type AttributeFilters,
  FactoryPreset,
  type GraphOverlayRequest,
  type GraphParameters,
  type GraphRequestBody,
  type GraphRequestSettings,
  type NodePositions,
  type PathLookupGraphSettings,
  type SnapshotId,
  type TopologyGraphSettings,
  graphSettingsSchema,
  isPathLookupGraphSettings,
  isTopologyGraphSettings,
} from '@ipfabric/utils';
import { clearSettings } from 'components/Graph/helpers/clearSettings';
import { extractPresetSettings } from 'components/Graph/helpers/extractPresetSettings';
import { nonEmptyObjectOrUndefined } from 'utils/object/nonEmptyObjectOrUndefined';
import type { ApiRequestParams } from 'utils/request/ApiRequestParams';

export interface GraphApiRequestArgs {
  overlay?: GraphOverlayRequest;
  parameters: GraphParameters;
  positions?: NodePositions;
  settings?: TopologyGraphSettings | PathLookupGraphSettings;
  snapshotId: SnapshotId;
  attributeFilters: AttributeFilters;
}

const processSettingsForRequest = (
  settings: TopologyGraphSettings | PathLookupGraphSettings,
): GraphRequestSettings => {
  if (isTopologyGraphSettings(settings)) {
    const requestSettings = extractPresetSettings(clearSettings(settings));
    if (graphSettingsSchema.validate(requestSettings).error) {
      throw new IpfError(
        'INVALID_ARGUMENT',
        'cannot fetch data, preset is invalid',
      );
    }
    return requestSettings;
  }
  if (isPathLookupGraphSettings(settings)) {
    return extractPresetSettings(settings);
  }
  throw new AbsurdIpfError(settings, 'Unknown graph settings type');
};

export const createGraphApiRequest = ({
  overlay,
  parameters,
  positions,
  settings = FactoryPreset,
  snapshotId,
  attributeFilters,
}: GraphApiRequestArgs): ApiRequestParams<GraphRequestBody> => ({
  url: '/graphs',
  method: 'POST',
  data: {
    overlay,
    parameters,
    positions,
    snapshot: snapshotId,
    attributeFilters: nonEmptyObjectOrUndefined(attributeFilters),
    settings: processSettingsForRequest(settings),
  },
});
